import React, { useState, useRef, createElement } from 'react';
import { motion, AnimatePresence, Reorder } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Save, Upload, Download, FileUp, CheckCircle, GripVertical, LogOut } from 'lucide-react';
import { Magnetic } from '../components/ui/Magnetic';
import { supabase } from '../lib/supabase';
import type { Project, ProjectsPageItem } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { migrateLocalStorageToSupabase } from '../lib/migrateData';
interface AdminPageProps {
  projects: Project[];
  projectsPageData: ProjectsPageItem[];
  onUpdateProjects: () => Promise<void>;
  onUpdateProjectsPageData: () => Promise<void>;
  onBack: () => void;
}
type Section = 'work' | 'projects';
export function AdminPage({
  projects,
  projectsPageData,
  onUpdateProjects,
  onUpdateProjectsPageData,
  onBack
}: AdminPageProps) {
  const [activeSection, setActiveSection] = useState<Section>('work');
  const [isEditing, setIsEditing] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editingProjectsItem, setEditingProjectsItem] = useState<ProjectsPageItem | null>(null);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: 'success' | 'error';
  }>({
    show: false,
    message: '',
    type: 'success'
  });
  const [formData, setFormData] = useState<Partial<Project>>({
    title: '',
    category: [],
    year: '2025',
    image: '',
    thumbnail: '',
    media_type: 'image',
    description: '',
    position: 0
  });
  const [projectsFormData, setProjectsFormData] = useState<Partial<ProjectsPageItem>>({
    title: '',
    description: '',
    image: '',
    link: '',
    category: 'Web App',
    button_text: 'View Project',
    button_disabled: false,
    position: 0
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isThumbnailDragging, setIsThumbnailDragging] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const importInputRef = useRef<HTMLInputElement>(null);
  const projectsImportInputRef = useRef<HTMLInputElement>(null);
  const {
    signOut,
    user
  } = useAuth();
  const categories = ['Design', 'Posters', 'Video', 'UX/UI', 'Apps'];
  const projectsCategories = ['NFT Collection', 'Web App'];
  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({
      show: true,
      message,
      type
    });
    setTimeout(() => {
      setNotification({
        show: false,
        message: '',
        type: 'success'
      });
    }, 3000);
  };
  const handleSignOut = async () => {
    await signOut();
    onBack();
  };
  const handleMigrate = async () => {
    if (!confirm('This will migrate data from localStorage to Supabase. Continue?')) {
      return;
    }
    setIsMigrating(true);
    const result = await migrateLocalStorageToSupabase();
    setIsMigrating(false);
    if (result.success) {
      showNotification(result.message, 'success');
      await onUpdateProjects();
      await onUpdateProjectsPageData();
    } else {
      showNotification(result.message, 'error');
    }
  };
  // Check if "Video" category is selected
  const isVideoCategory = () => {
    const categories = Array.isArray(formData.category) ? formData.category : formData.category ? [formData.category] : [];
    return categories.includes('Video');
  };
  const handleEdit = (project: Project) => {
    setEditingProject(project);
    setFormData({
      ...project,
      category: project.category
    });
    setIsEditing(true);
  };
  const handleEditProjectsItem = (item: ProjectsPageItem) => {
    setEditingProjectsItem(item);
    setProjectsFormData(item);
    setIsEditing(true);
  };
  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      const {
        error
      } = await supabase.from('projects').delete().eq('id', id);
      if (error) {
        showNotification('Error deleting project', 'error');
        console.error(error);
        return;
      }
      showNotification('Project deleted successfully', 'success');
      await onUpdateProjects();
    }
  };
  const handleDeleteProjectsItem = async (id: string) => {
    if (confirm('Are you sure you want to delete this project?')) {
      const {
        error
      } = await supabase.from('projects_page').delete().eq('id', id);
      if (error) {
        showNotification('Error deleting project', 'error');
        console.error(error);
        return;
      }
      showNotification('Project deleted successfully', 'success');
      await onUpdateProjectsPageData();
    }
  };
  const handleCategoryToggle = (cat: string) => {
    const currentCategories = Array.isArray(formData.category) ? formData.category : formData.category ? [formData.category] : [];
    const newCategories = currentCategories.includes(cat) ? currentCategories.filter(c => c !== cat) : [...currentCategories, cat];
    setFormData({
      ...formData,
      category: newCategories
    });
  };
  const handleFileChange = (file: File) => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    if (!isImage && !isVideo) {
      showNotification('Please upload a valid image or video file', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (activeSection === 'work') {
        setFormData({
          ...formData,
          image: reader.result as string,
          media_type: isVideo ? 'video' : 'image'
        });
      } else {
        setProjectsFormData({
          ...projectsFormData,
          image: reader.result as string
        });
      }
    };
    reader.readAsDataURL(file);
  };
  const handleThumbnailChange = (file: File) => {
    if (!file.type.startsWith('image/')) {
      showNotification('Please upload a valid image file for thumbnail', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({
        ...formData,
        thumbnail: reader.result as string
      });
    };
    reader.readAsDataURL(file);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };
  const handleThumbnailDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsThumbnailDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleThumbnailChange(file);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (activeSection === 'work') {
      const categoryArray = Array.isArray(formData.category) ? formData.category : formData.category ? [formData.category] : [];
      if (!formData.title || !formData.image || categoryArray.length === 0) {
        showNotification('Title, Image, and at least one Category are required', 'error');
        return;
      }
      if (categoryArray.includes('Video') && !formData.thumbnail) {
        showNotification('Thumbnail is required for Video category', 'error');
        return;
      }
      const projectData = {
        title: formData.title,
        category: categoryArray,
        year: formData.year || '2025',
        image: formData.image,
        thumbnail: formData.thumbnail,
        media_type: formData.media_type || 'image',
        description: formData.description,
        position: editingProject ? editingProject.position : projects.length
      };
      if (editingProject) {
        const {
          error
        } = await supabase.from('projects').update(projectData).eq('id', editingProject.id);
        if (error) {
          showNotification('Error updating project', 'error');
          console.error(error);
          return;
        }
        showNotification('Project updated successfully!', 'success');
      } else {
        const {
          error
        } = await supabase.from('projects').insert([projectData]);
        if (error) {
          showNotification('Error creating project', 'error');
          console.error(error);
          return;
        }
        showNotification('Project created successfully!', 'success');
      }
      await onUpdateProjects();
    } else {
      if (!projectsFormData.title || !projectsFormData.description || !projectsFormData.image || !projectsFormData.category) {
        showNotification('Title, Description, Image, and Category are required', 'error');
        return;
      }
      const projectData = {
        title: projectsFormData.title,
        description: projectsFormData.description,
        image: projectsFormData.image,
        link: projectsFormData.link,
        category: projectsFormData.category,
        button_text: projectsFormData.button_text || 'View Project',
        button_disabled: projectsFormData.button_disabled || false,
        position: editingProjectsItem ? editingProjectsItem.position : projectsPageData.length
      };
      if (editingProjectsItem) {
        const {
          error
        } = await supabase.from('projects_page').update(projectData).eq('id', editingProjectsItem.id);
        if (error) {
          showNotification('Error updating project', 'error');
          console.error(error);
          return;
        }
        showNotification('Project updated successfully!', 'success');
      } else {
        const {
          error
        } = await supabase.from('projects_page').insert([projectData]);
        if (error) {
          showNotification('Error creating project', 'error');
          console.error(error);
          return;
        }
        showNotification('Project created successfully!', 'success');
      }
      await onUpdateProjectsPageData();
    }
    setIsEditing(false);
    setEditingProject(null);
    setEditingProjectsItem(null);
    setTimeout(() => {
      setFormData({
        title: '',
        category: [],
        year: '2025',
        image: '',
        thumbnail: '',
        media_type: 'image',
        description: '',
        position: 0
      });
      setProjectsFormData({
        title: '',
        description: '',
        image: '',
        link: '',
        category: 'Web App',
        button_text: 'View Project',
        button_disabled: false,
        position: 0
      });
    }, 100);
  };
  const handleCancel = () => {
    setIsEditing(false);
    setEditingProject(null);
    setEditingProjectsItem(null);
    setFormData({
      title: '',
      category: [],
      year: '2025',
      image: '',
      thumbnail: '',
      media_type: 'image',
      description: '',
      position: 0
    });
    setProjectsFormData({
      title: '',
      description: '',
      image: '',
      link: '',
      category: 'Web App',
      button_text: 'View Project',
      button_disabled: false,
      position: 0
    });
  };
  const handleNewProject = () => {
    setEditingProject(null);
    setEditingProjectsItem(null);
    setFormData({
      title: '',
      category: [],
      year: '2025',
      image: '',
      thumbnail: '',
      media_type: 'image',
      description: '',
      position: 0
    });
    setProjectsFormData({
      title: '',
      description: '',
      image: '',
      link: '',
      category: 'Web App',
      button_text: 'View Project',
      button_disabled: false,
      position: 0
    });
    setIsEditing(true);
  };
  const handleExport = () => {
    const data = activeSection === 'work' ? projects : projectsPageData;
    const dataStr = JSON.stringify(data, null, 2);
    const dataBlob = new Blob([dataStr], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeSection}-export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showNotification(`${activeSection === 'work' ? 'Work' : 'Projects'} exported successfully!`, 'success');
  };
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'application/json') {
      showNotification('Please upload a valid JSON file', 'error');
      return;
    }
    const reader = new FileReader();
    reader.onload = async event => {
      try {
        const importedData = JSON.parse(event.target?.result as string);
        if (!Array.isArray(importedData)) {
          showNotification('Invalid JSON format: Expected an array', 'error');
          return;
        }
        const currentData = activeSection === 'work' ? projects : projectsPageData;
        if (confirm(`This will replace all ${currentData.length} existing items with ${importedData.length} imported items. Continue?`)) {
          if (activeSection === 'work') {
            // Delete all existing
            await supabase.from('projects').delete().neq('id', '');
            // Insert new data
            const {
              error
            } = await supabase.from('projects').insert(importedData);
            if (error) throw error;
            await onUpdateProjects();
          } else {
            // Delete all existing
            await supabase.from('projects_page').delete().neq('id', '');
            // Insert new data
            const {
              error
            } = await supabase.from('projects_page').insert(importedData);
            if (error) throw error;
            await onUpdateProjectsPageData();
          }
          showNotification(`${activeSection === 'work' ? 'Work' : 'Projects'} imported successfully!`, 'success');
        }
      } catch (error) {
        showNotification('Error importing data', 'error');
        console.error('Import error:', error);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };
  const handleReorder = async (newOrder: any[]) => {
    // Update positions in the reordered array
    const updatedData = newOrder.map((item, index) => ({
      ...item,
      position: index
    }));
    if (activeSection === 'work') {
      // Update all positions in database
      for (const item of updatedData) {
        await supabase.from('projects').update({
          position: item.position
        }).eq('id', item.id);
      }
      await onUpdateProjects();
    } else {
      // Update all positions in database
      for (const item of updatedData) {
        await supabase.from('projects_page').update({
          position: item.position
        }).eq('id', item.id);
      }
      await onUpdateProjectsPageData();
    }
  };
  const getCategoryDisplay = (category: string | string[]) => {
    if (Array.isArray(category)) {
      return category.join(', ');
    }
    return category;
  };
  const getDisplayImage = (project: Project) => {
    const categories = Array.isArray(project.category) ? project.category : [project.category];
    if (categories.includes('Video') && project.thumbnail) {
      return project.thumbnail;
    }
    return project.image;
  };
  const currentData = activeSection === 'work' ? projects : projectsPageData;
  return <div className="bg-black text-white min-h-screen selection:bg-white selection:text-black" data-id="element-193">
      {/* Notification Toast */}
      <AnimatePresence data-id="element-194">
        {notification.show && <motion.div initial={{
        opacity: 0,
        y: -50
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -50
      }} className="fixed top-24 right-8 z-50 p-4 rounded-sm border flex items-center gap-3 min-w-[300px]" style={{
        backgroundColor: notification.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
        borderColor: notification.type === 'success' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'
      }} data-id="element-195">
            <CheckCircle className="w-5 h-5 flex-shrink-0" style={{
          color: notification.type === 'success' ? '#22c55e' : '#ef4444'
        }} data-id="element-196" />
            <p style={{
          color: notification.type === 'success' ? '#22c55e' : '#ef4444'
        }} data-id="element-197">
              {notification.message}
            </p>
          </motion.div>}
      </AnimatePresence>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 w-full p-6 md:p-12 flex justify-between items-center z-40 bg-black/80 backdrop-blur-sm border-b border-white/10" data-id="element-198">
        <Magnetic data-id="element-199">
          <button onClick={onBack} className="text-2xl font-black tracking-tighter transition-opacity duration-300 hover:opacity-70" data-id="element-200">
            Jav.
          </button>
        </Magnetic>
        <div className="flex items-center gap-4" data-id="element-201">
          <span className="text-sm uppercase tracking-widest font-light" style={{
          color: '#999'
        }} data-id="element-202">
            {user?.email}
          </span>
          <button onClick={handleSignOut} className="px-4 py-2 border border-white/20 text-white font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-203">
            <LogOut className="w-4 h-4" data-id="element-204" />
            <span className="hidden md:inline" data-id="element-205">Sign Out</span>
          </button>
        </div>
      </nav>

      <main className="pt-48 pb-24 px-4 md:px-12" data-id="element-206">
        <div className="max-w-7xl mx-auto" data-id="element-207">
          {/* Migration Button */}
          {projects.length === 0 && projectsPageData.length === 0 && <div className="mb-8 p-6 border border-yellow-500/30 bg-yellow-500/10 rounded-sm" data-id="element-208">
              <h3 className="text-lg font-bold mb-2 text-yellow-500" data-id="element-209">
                First Time Setup
              </h3>
              <p className="text-sm text-yellow-500/80 mb-4" data-id="element-210">
                Migrate your existing data from localStorage to Supabase
              </p>
              <button onClick={handleMigrate} disabled={isMigrating} className="px-6 py-3 bg-yellow-500 text-black font-bold rounded-full transition-opacity duration-300 hover:opacity-70 disabled:opacity-50" data-id="element-211">
                {isMigrating ? 'Migrating...' : 'Migrate Data'}
              </button>
            </div>}

          {/* Section Tabs */}
          <div className="flex gap-4 mb-8 border-b border-white/20 pb-4" data-id="element-212">
            <button onClick={() => {
            setActiveSection('work');
            setIsEditing(false);
          }} className={`px-6 py-3 rounded-full font-bold transition-all ${activeSection === 'work' ? 'bg-white text-black' : 'bg-transparent text-white border border-white/20 hover:border-white/40'}`} data-id="element-213">
              Work (Portfolio)
            </button>
            <button onClick={() => {
            setActiveSection('projects');
            setIsEditing(false);
          }} className={`px-6 py-3 rounded-full font-bold transition-all ${activeSection === 'projects' ? 'bg-white text-black' : 'bg-transparent text-white border border-white/20 hover:border-white/40'}`} data-id="element-214">
              Projects (Projects Page)
            </button>
          </div>

          {/* Header */}
          <div className="flex items-end justify-between mb-16 border-b border-white/20 pb-8" data-id="element-215">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight" data-id="element-216">
              Manage {activeSection === 'work' ? 'Work' : 'Projects'}
            </h1>
            <div className="flex gap-3" data-id="element-217">
              <button onClick={() => activeSection === 'work' ? importInputRef.current?.click() : projectsImportInputRef.current?.click()} className="px-4 py-3 border border-white/20 text-white font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-218">
                <FileUp className="w-4 h-4" data-id="element-219" />
                <span className="hidden md:inline" data-id="element-220">Import</span>
              </button>
              <input ref={importInputRef} type="file" accept="application/json" onChange={handleImport} className="hidden" data-id="element-221" />
              <input ref={projectsImportInputRef} type="file" accept="application/json" onChange={handleImport} className="hidden" data-id="element-222" />
              <button onClick={handleExport} className="px-4 py-3 border border-white/20 text-white font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-223">
                <Download className="w-4 h-4" data-id="element-224" />
                <span className="hidden md:inline" data-id="element-225">Export</span>
              </button>
              <button onClick={handleNewProject} className="px-6 py-3 bg-white text-black font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-226">
                <Plus className="w-4 h-4" data-id="element-227" />
                Add {activeSection === 'work' ? 'Project' : 'Item'}
              </button>
            </div>
          </div>

          {/* Form with Sidebar Preview */}
          {isEditing && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="mb-16 border border-white/20 rounded-sm bg-neutral-950" data-id="element-228">
              <div className="flex flex-col lg:flex-row" data-id="element-229">
                {/* Main Form Area */}
                <div className="flex-1 p-8" data-id="element-230">
                  <div className="flex items-center justify-between mb-6" data-id="element-231">
                    <h2 className="text-2xl font-bold tracking-tight uppercase font-light" style={{
                  color: '#999'
                }} data-id="element-232">
                      {activeSection === 'work' ? editingProject ? 'Edit Project' : 'New Project' : editingProjectsItem ? 'Edit Project' : 'New Project'}
                    </h2>
                    <button onClick={handleCancel} className="transition-opacity duration-300 hover:opacity-70" data-id="element-233">
                      <X className="w-6 h-6" data-id="element-234" />
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-6" data-id="element-235">
                    <div data-id="element-236">
                      <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                    color: '#999'
                  }} data-id="element-237">
                        Title *
                      </label>
                      <input type="text" value={activeSection === 'work' ? formData.title : projectsFormData.title} onChange={e => activeSection === 'work' ? setFormData({
                    ...formData,
                    title: e.target.value
                  }) : setProjectsFormData({
                    ...projectsFormData,
                    title: e.target.value
                  })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors" required data-id="element-238" />
                    </div>

                    {activeSection === 'projects' && <>
                        <div data-id="element-239">
                          <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                      color: '#999'
                    }} data-id="element-240">
                            Description *
                          </label>
                          <textarea value={projectsFormData.description} onChange={e => setProjectsFormData({
                      ...projectsFormData,
                      description: e.target.value
                    })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors h-32 resize-none" required data-id="element-241" />
                        </div>
                        <div data-id="element-242">
                          <label className="block text-sm uppercase tracking-widest font-light mb-3" style={{
                      color: '#999'
                    }} data-id="element-243">
                            Category *
                          </label>
                          <div className="flex flex-wrap gap-3" data-id="element-244">
                            {projectsCategories.map(cat => {
                        const isSelected = projectsFormData.category === cat;
                        return <button key={cat} type="button" onClick={() => setProjectsFormData({
                          ...projectsFormData,
                          category: cat
                        })} className={`px-4 py-2 rounded-full border transition-all ${isSelected ? 'bg-white text-black border-white' : 'bg-transparent text-white border-white/20 hover:border-white/40'}`} data-id="element-245">
                                  {cat}
                                </button>;
                      })}
                          </div>
                        </div>
                        <div data-id="element-246">
                          <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                      color: '#999'
                    }} data-id="element-247">
                            External Link
                          </label>
                          <input type="url" value={projectsFormData.link} onChange={e => setProjectsFormData({
                      ...projectsFormData,
                      link: e.target.value
                    })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors" placeholder="https://example.com (optional)" data-id="element-248" />
                          <p className="text-xs mt-2" style={{
                      color: '#666'
                    }} data-id="element-249">
                            Optional - Leave empty if button should be disabled
                          </p>
                        </div>
                        <div data-id="element-250">
                          <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                      color: '#999'
                    }} data-id="element-251">
                            Button Text
                          </label>
                          <input type="text" value={projectsFormData.button_text} onChange={e => setProjectsFormData({
                      ...projectsFormData,
                      button_text: e.target.value
                    })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors" placeholder="View Project" data-id="element-252" />
                          <p className="text-xs mt-2" style={{
                      color: '#666'
                    }} data-id="element-253">
                            Default: "View Project"
                          </p>
                        </div>
                        <div data-id="element-254">
                          <label className="flex items-center gap-3 cursor-pointer" data-id="element-255">
                            <input type="checkbox" checked={projectsFormData.button_disabled || false} onChange={e => setProjectsFormData({
                        ...projectsFormData,
                        button_disabled: e.target.checked
                      })} className="w-5 h-5 rounded border-white/20 bg-black checked:bg-white checked:border-white transition-colors cursor-pointer" data-id="element-256" />
                            <div data-id="element-257">
                              <span className="text-sm uppercase tracking-widest font-light" style={{
                          color: '#999'
                        }} data-id="element-258">
                                Disable Button
                              </span>
                              <p className="text-xs mt-1" style={{
                          color: '#666'
                        }} data-id="element-259">
                                Button will show with reduced opacity and no
                                link
                              </p>
                            </div>
                          </label>
                        </div>
                      </>}

                    {activeSection === 'work' && <>
                        <div data-id="element-260">
                          <label className="block text-sm uppercase tracking-widest font-light mb-3" style={{
                      color: '#999'
                    }} data-id="element-261">
                            Categories * (Select one or more)
                          </label>
                          <div className="flex flex-wrap gap-3" data-id="element-262">
                            {categories.map(cat => {
                        const isSelected = Array.isArray(formData.category) ? formData.category.includes(cat) : formData.category === cat;
                        return <button key={cat} type="button" onClick={() => handleCategoryToggle(cat)} className={`px-4 py-2 rounded-full border transition-all ${isSelected ? 'bg-white text-black border-white' : 'bg-transparent text-white border-white/20 hover:border-white/40'}`} data-id="element-263">
                                  {cat}
                                </button>;
                      })}
                          </div>
                        </div>
                        <div data-id="element-264">
                          <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                      color: '#999'
                    }} data-id="element-265">
                            Year
                          </label>
                          <input type="text" value={formData.year} onChange={e => setFormData({
                      ...formData,
                      year: e.target.value
                    })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors" data-id="element-266" />
                        </div>
                        <div data-id="element-267">
                          <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                      color: '#999'
                    }} data-id="element-268">
                            Description (Optional)
                          </label>
                          <textarea value={formData.description} onChange={e => setFormData({
                      ...formData,
                      description: e.target.value
                    })} className="w-full bg-black border border-white/20 rounded-sm px-4 py-3 text-white focus:outline-none focus:border-white/40 transition-colors h-32 resize-none" placeholder="Project description..." data-id="element-269" />
                        </div>
                      </>}

                    {/* File Upload */}
                    <div data-id="element-270">
                      <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                    color: '#999'
                  }} data-id="element-271">
                        Image *
                      </label>
                      <div onDrop={handleDrop} onDragOver={e => {
                    e.preventDefault();
                    setIsDragging(true);
                  }} onDragLeave={() => setIsDragging(false)} onClick={() => fileInputRef.current?.click()} className={`relative w-full min-h-[120px] border-2 border-dashed rounded-sm flex flex-col items-center justify-center gap-3 cursor-pointer transition-all ${isDragging ? 'border-white/60 bg-white/5' : 'border-white/20 hover:border-white/40 hover:bg-white/5'}`} data-id="element-272">
                        <Upload className="w-8 h-8 text-white/40" data-id="element-273" />
                        <div className="text-center" data-id="element-274">
                          <p className="text-white/60 text-sm mb-1" data-id="element-275">
                            Drag & drop an image here
                          </p>
                          <p className="text-white/40 text-xs" data-id="element-276">
                            or click to browse
                          </p>
                        </div>
                      </div>
                      <input ref={fileInputRef} type="file" accept="image/*" onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileChange(file);
                  }} className="hidden" data-id="element-277" />
                    </div>

                    {activeSection === 'work' && isVideoCategory() && <div data-id="element-278">
                        <label className="block text-sm uppercase tracking-widest font-light mb-2" style={{
                    color: '#999'
                  }} data-id="element-279">
                          Thumbnail Image * (for grid display)
                        </label>
                        <div onDrop={handleThumbnailDrop} onDragOver={e => {
                    e.preventDefault();
                    setIsThumbnailDragging(true);
                  }} onDragLeave={() => setIsThumbnailDragging(false)} onClick={() => thumbnailInputRef.current?.click()} className={`relative w-full min-h-[120px] border-2 border-dashed rounded-sm flex flex-col items-center justify-center gap-3 cursor-pointer transition-all ${isThumbnailDragging ? 'border-white/60 bg-white/5' : 'border-white/20 hover:border-white/40 hover:bg-white/5'}`} data-id="element-280">
                          <Upload className="w-8 h-8 text-white/40" data-id="element-281" />
                          <div className="text-center" data-id="element-282">
                            <p className="text-white/60 text-sm mb-1" data-id="element-283">
                              Drag & drop thumbnail image here
                            </p>
                            <p className="text-white/40 text-xs" data-id="element-284">
                              or click to browse
                            </p>
                          </div>
                        </div>
                        <input ref={thumbnailInputRef} type="file" accept="image/*" onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleThumbnailChange(file);
                  }} className="hidden" data-id="element-285" />
                      </div>}

                    <div className="flex gap-4" data-id="element-286">
                      <button type="submit" className="px-8 py-4 bg-white text-black font-bold rounded-full flex items-center gap-2 transition-opacity duration-300 hover:opacity-70" data-id="element-287">
                        <Save className="w-4 h-4" data-id="element-288" />
                        {activeSection === 'work' ? editingProject ? 'Update' : 'Create' : editingProjectsItem ? 'Update' : 'Create'}
                      </button>
                      <button type="button" onClick={handleCancel} className="px-8 py-4 border border-white/20 text-white font-bold rounded-full transition-opacity duration-300 hover:opacity-70" data-id="element-289">
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>

                {/* Sidebar Preview */}
                {(activeSection === 'work' && formData.image || activeSection === 'projects' && projectsFormData.image) && <div className="lg:w-80 border-t lg:border-t-0 lg:border-l border-white/20 p-8 bg-black" data-id="element-290">
                    <h3 className="text-sm uppercase tracking-widest font-light mb-4" style={{
                color: '#999'
              }} data-id="element-291">
                      Preview
                    </h3>
                    <div className="space-y-4" data-id="element-292">
                      {activeSection === 'work' ? <>
                          {/* Grid Preview - Show thumbnail if Video category */}
                          {isVideoCategory() && formData.thumbnail && <div data-id="element-293">
                              <p className="text-xs uppercase tracking-widest font-light mb-2" style={{
                      color: '#999'
                    }} data-id="element-294">
                                Grid Thumbnail
                              </p>
                              <div className="w-full aspect-[4/3] bg-neutral-900 rounded-sm overflow-hidden" data-id="element-295">
                                <img src={formData.thumbnail} alt="Thumbnail Preview" className="w-full h-full object-cover" data-id="element-296" />
                              </div>
                            </div>}

                          {/* Full Media Preview */}
                          <div data-id="element-297">
                            <p className="text-xs uppercase tracking-widest font-light mb-2" style={{
                      color: '#999'
                    }} data-id="element-298">
                              {isVideoCategory() ? 'Full Video' : 'Full Media'}
                            </p>
                            <div className="w-full aspect-[4/3] bg-neutral-900 rounded-sm overflow-hidden" data-id="element-299">
                              {formData.mediaType === 'video' ? <video src={formData.image} autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-300" /> : <img src={formData.image} alt="Preview" className="w-full h-full object-cover" data-id="element-301" />}
                            </div>
                          </div>

                          {formData.title && <div data-id="element-302">
                              <p className="text-lg font-bold" data-id="element-303">
                                {formData.title}
                              </p>
                              <p className="text-sm text-white/60" data-id="element-304">
                                {getCategoryDisplay(formData.category || [])} •{' '}
                                {formData.year}
                              </p>
                            </div>}
                        </> : <>
                          {/* Projects Page Preview */}
                          <div data-id="element-305">
                            <p className="text-xs uppercase tracking-widest font-light mb-2" style={{
                      color: '#999'
                    }} data-id="element-306">
                              Image Preview
                            </p>
                            <div className="w-full bg-neutral-900 rounded-sm overflow-hidden" data-id="element-307">
                              <img src={projectsFormData.image} alt="Preview" className="w-full h-auto object-contain" data-id="element-308" />
                            </div>
                          </div>

                          {projectsFormData.title && <div data-id="element-309">
                              {projectsFormData.category && <span className="inline-block px-2 py-1 rounded-full text-xs uppercase tracking-wider font-medium mb-2" style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgb(163, 163, 163)'
                    }} data-id="element-310">
                                  {projectsFormData.category}
                                </span>}
                              <p className="text-lg font-bold" data-id="element-311">
                                {projectsFormData.title}
                              </p>
                              {projectsFormData.description && <p className="text-sm text-white/60 mt-2 mb-4" data-id="element-312">
                                  {projectsFormData.description.substring(0, 80)}
                                  ...
                                </p>}
                              {/* Button Preview */}
                              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm ${projectsFormData.button_disabled ? 'bg-white/30 text-white/50 cursor-not-allowed' : 'bg-white text-black'}`} data-id="element-313">
                                {projectsFormData.button_text || 'View Project'}
                              </div>
                            </div>}
                        </>}
                    </div>
                  </div>}
              </div>
            </motion.div>}

          {/* List */}
          <div className="space-y-4" data-id="element-314">
            {currentData.length === 0 ? <div className="text-center py-16" data-id="element-315">
                <p className="text-xl" style={{
              color: '#999'
            }} data-id="element-316">
                  No {activeSection === 'work' ? 'work' : 'projects'} yet. Add
                  some above.
                </p>
              </div> : <Reorder.Group axis="y" values={currentData} onReorder={handleReorder} className="space-y-4" data-id="element-317">
                {currentData.map((item: any) => <Reorder.Item key={item.id} value={item} className="p-6 border border-white/20 rounded-sm bg-neutral-950 flex items-center gap-6 hover:border-white/40 transition-colors cursor-grab active:cursor-grabbing" whileDrag={{
              scale: 1.02,
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }} data-id="element-318">
                    {/* Drag Handle */}
                    <div className="flex-shrink-0 cursor-grab active:cursor-grabbing" data-id="element-319">
                      <GripVertical className="w-5 h-5" style={{
                  color: '#666'
                }} data-id="element-320" />
                    </div>

                    <div className="w-32 h-20 bg-neutral-900 rounded-sm overflow-hidden flex-shrink-0" data-id="element-321">
                      <img src={activeSection === 'work' ? getDisplayImage(item) : item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-322" />
                    </div>
                    <div className="flex-1" data-id="element-323">
                      <h3 className="text-xl font-bold mb-1" data-id="element-324">{item.title}</h3>
                      <p className="text-sm" style={{
                  color: '#999'
                }} data-id="element-325">
                        {activeSection === 'work' ? `${getCategoryDisplay(item.category)} • ${item.year}` : `${item.category || 'Uncategorized'} • ${item.description.substring(0, 60)}...`}
                      </p>
                    </div>
                    <div className="flex gap-2" data-id="element-326">
                      <button onClick={() => activeSection === 'work' ? handleEdit(item) : handleEditProjectsItem(item)} className="p-3 border border-white/20 rounded-full transition-opacity duration-300 hover:opacity-70" aria-label="Edit" data-id="element-327">
                        <Edit2 className="w-4 h-4" data-id="element-328" />
                      </button>
                      <button onClick={() => activeSection === 'work' ? handleDelete(item.id) : handleDeleteProjectsItem(item.id)} className="p-3 border border-white/20 rounded-full transition-opacity duration-300 hover:opacity-70" aria-label="Delete" data-id="element-329">
                        <Trash2 className="w-4 h-4" data-id="element-330" />
                      </button>
                    </div>
                  </Reorder.Item>)}
              </Reorder.Group>}
          </div>
        </div>
      </main>
    </div>;
}